import { Col, Progress, Row, Tooltip } from 'antd';
import { Rule } from 'antd/lib/form';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { getDateWithTime } from '../../../utils/dateTimeUtils';
import { GUTTOR_SPACE } from '../../constants/commonConstants';
import { EXTENDED_STATUS, HIS_UPLOAD_STATUS, NEW_UPLOAD_STATUS, progress, UPLOAD_STATUS } from '../../constants/crmConstants';
import { ReactComponent as FileIcon } from '../../../assets/images/svg/i_file.svg';
import { UploadFile } from 'antd/lib/upload/interface';
import { submitAdmission } from '../../../store/actions/fileAction';
import { useDispatch, useSelector } from 'react-redux';
import { centeredNotification } from '../../../utils/utility';
import { RootState } from '../../../store';
import { NewAdmissionState } from '../../../store/models/newAdmissionModel';
import { setBuddhimedSelectedFiles, setPreviouslyUploadedFiles, setUploadedFiles } from '../../../store/actions/newAdmissionAction';
import { FormCheckBox } from '../crmFormElement/FormInput';
import { getFileType } from '../../../utils/fileUploadUtils';

function HISAvailableUploadListItem({ file, index, onRemove, fileListLength, selectionAllowed = false,
  newUploads, showProgress, isDocumentTypeRequired = true, isHISFlow, form, numberOfFilesLimit, updateErrorMessage }: any): ReactElement {
  const dispatch = useDispatch();
  const rules: Rule[] = [];
  const [progress33, progress66, progress100] = progress;
  const {
    uploadedFiles, selectedFiles, previouslyUploadedFiles,
  } = useSelector((state: RootState): NewAdmissionState => state.newAdmission);
  const [toggle, isToggle] = useState(false);

  rules.push({ required: true });
  const { existingRecord, name, createdDate, status, extendedStatus, fileType = [] } = file;
  const newFileUploads = uploadedFiles?.filter((fileUid: any): any => fileUid?.claimStatus === NEW_UPLOAD_STATUS);

  const onDownload = (aFile: UploadFile): any => {
    const { response } = aFile;
    if (response) {
      const { errorMsg, uploadedDetails, isSuccess } = response;
      if (isSuccess) {
        const { fileGuid, fileName } = uploadedDetails[0];
        centeredNotification(`${fileName} start downloading...`, 'success');
        dispatch(submitAdmission(fileGuid));
      } else {
        centeredNotification(errorMsg);
      }
    }
  };

  const getFileStatus = (): string => {
    if (status !== UPLOAD_STATUS.done) {
      return status === UPLOAD_STATUS.uploading ? 'Uploading...' : '';
    }
    if (extendedStatus === EXTENDED_STATUS.verified || existingRecord) {
      return 'Verified';
    }
    return extendedStatus === EXTENDED_STATUS.verifying ? 'Verifying...' : 'Uploaded';
  };

  let customPercent = 0;
  if (status === UPLOAD_STATUS.uploading) {
    customPercent = progress33;
  } else if (status === UPLOAD_STATUS.done) {
    customPercent = (extendedStatus === EXTENDED_STATUS.verified
      || extendedStatus === EXTENDED_STATUS.uploaded || (!extendedStatus && status === UPLOAD_STATUS.done)) ? progress100 : progress66;
  }

  const newUploadedFiles = [...uploadedFiles];

  const tempUploadedFiles = newUploadedFiles?.filter(
    (obj: any): any =>
      (obj?.status === NEW_UPLOAD_STATUS || obj?.status === HIS_UPLOAD_STATUS || !obj?.existingRecord),
  );

  const onSelectToggle = useCallback(
    (event: any, fileItem: any): any => {
      const checked = event?.target?.checked;
      let newData = [...selectedFiles];
      let checkData = [...previouslyUploadedFiles];
      // Toggle the state to trigger a UI refresh or other dependent functionality
      isToggle(!toggle);
      if (checked) {
        // Logic for when the file is selected (checkbox is checked)
        fileItem.isDeleted = false;
        fileItem.isRejected = false;
        fileItem.discrepancy = false;
        fileItem.size = fileItem?.size;
        checkData?.push(fileItem);
        dispatch(setPreviouslyUploadedFiles(checkData));
        // Check if the total uploaded files are below the limit
        if (tempUploadedFiles?.length < numberOfFilesLimit) {
          fileItem.existingRecord = false;
          fileItem.availableDocument = true;
          newData?.push(fileItem);
          dispatch(setBuddhimedSelectedFiles(newData));
        } else {
          updateErrorMessage(`Maximum file allowed is ${numberOfFilesLimit}`);
        }

      } else {
        // Find the index of the file in the relevant arrays based on its unique identifier
        // This is used to identify the file's position within the selected files for further operations
        const objIndex = selectedFiles?.findIndex(
          (obj: any): any => obj?.uid === fileItem?.uid,
        );
        // This helps locate the file within the uploaded files for updating its properties (e.g., marking as unavailable)
        const updatedIndex = uploadedFiles?.findIndex(
          (obj: any): any => obj?.uid === fileItem?.uid,
        );
        // This is used to determine if the file exists in the previously uploaded files list for removal or updates
        const checkedIndex = previouslyUploadedFiles?.findIndex(
          (obj: any): any => obj?.uid === fileItem?.uid,
        );
        // Remove the file from the previously uploaded files list if it exists
        if (checkedIndex !== -1) {
          previouslyUploadedFiles?.splice(checkedIndex, 1);
          dispatch(setPreviouslyUploadedFiles(previouslyUploadedFiles));
        } else {
          checkData = [];
          dispatch(setPreviouslyUploadedFiles(checkData));
        }
        updateErrorMessage('');
        if (objIndex !== -1 && !checked) {
          // If the file exists in the selected files list and is now unchecked

          // Remove the file from the selected files list
          selectedFiles?.splice(objIndex, 1);
          const updatedFiles = [...uploadedFiles];
          // Update the uploaded files list to mark the file as an existing record and unavailable

          updatedFiles[updatedIndex] = {
            ...updatedFiles[updatedIndex],
            existingRecord: true,
            availableDocument: false,
          };
          dispatch(setBuddhimedSelectedFiles(selectedFiles));
          dispatch(setUploadedFiles([...updatedFiles]));
        } else {
          newData = [];
          dispatch(setBuddhimedSelectedFiles(newData));
        }
      }
    },
    [selectedFiles, uploadedFiles, newFileUploads, numberOfFilesLimit, dispatch, toggle], // Dependencies
  );

  const newFileItemUploads = uploadedFiles?.filter((fileUid: any): any => fileUid?.claimStatus === NEW_UPLOAD_STATUS);
  const hisFileUploads = uploadedFiles?.filter((fileUid: any): any => fileUid?.claimStatus === HIS_UPLOAD_STATUS);
  const existingFileUploads = uploadedFiles?.filter((fileUid: any): any => fileUid?.existingRecord === false);

  const data = newFileItemUploads?.length + hisFileUploads?.length;
  const totalUploadedFiles = newFileUploads?.length + hisFileUploads?.length + existingFileUploads?.length;

  const updateFormValues = useCallback((): any => {
    const currentFormValues = form?.getFieldsValue();
    if (totalUploadedFiles <= numberOfFilesLimit && selectedFiles?.length) {

      // Create a copy of currentFormValues to avoid overwriting in each iteration
      const updatedFormKeyValues = { ...currentFormValues };
      selectedFiles?.forEach((fileItemRecord: any): any => {
        const hisFileUIDs = new Set(selectedFiles?.map((id: any): any => `avl_doc_${id?.uid}`));

        Object?.keys(currentFormValues)?.forEach((key: string): any => {
          if (key?.startsWith('avl_doc_')) {
            // Check conditions and update the corresponding field in updatedFormValues
            if (hisFileUIDs?.has(key)) {
              updatedFormKeyValues[key] = key; // Mark checkbox as checked
            } else {
              updatedFormKeyValues[key] = undefined;
            }
          }
        });
      });
      form?.setFieldsValue(updatedFormKeyValues); // Update the form with the final values
    } else {
      // If no files are selected, uncheck all related checkboxes
      Object?.keys(currentFormValues)?.forEach((key: string): any => {
        if (key?.startsWith('avl_doc_')) {
          currentFormValues[key] = undefined;
        }
      });
      form?.setFieldsValue(currentFormValues);
    }
  }, [form, selectedFiles, data, previouslyUploadedFiles]); // Dependencies

  useEffect((): any => {
    updateFormValues();
  }, [selectedFiles, updateFormValues, toggle, tempUploadedFiles, data, previouslyUploadedFiles]);

  return (
    <div className="upload-list available-provider-documents">
      <Row gutter={GUTTOR_SPACE}>
        <Col md={6}>
          <div className="text-elipsis"><span className="file-count">
            {showProgress ? index + 1 : fileListLength - index}</span>
            <FileIcon className="fill-gray v-align-middle" />
            <span className="text-primary">
              <a onClick={(): void => onDownload(file)}>
                <Tooltip placement="right" title={name?.length > 16 ? name : null} mouseEnterDelay={0.8}>
                  {name}
                </Tooltip>
              </a>
            </span>
          </div>
        </Col>
        <Col md={!isDocumentTypeRequired ? 8 : 6} className="avldocs-date">
          {createdDate || getDateWithTime(new Date())}
        </Col>
        <Col md={1} className="circular-progress avldocs-date">
          {<Progress type="circle" percent={customPercent} width={20} strokeColor="#2ABF86" />}
        </Col>
        {
          !newUploads ? <Col md={!isDocumentTypeRequired ? 7 : 4} className="avldocs-status">
            <span className="text-primary bold fsize_sm">
              {getFileStatus()}
            </span>
          </Col> : <></>
        }
        {
          isDocumentTypeRequired &&
          <Col md={newUploads ? 7 : 5} className="avldocs-filetype">
            {
              getFileType(fileType)
            }
          </Col>
        }
        <Col span={1} className="avldocs-selection">
          {selectionAllowed && <FormCheckBox
            detail={{
              key: `avl_doc_${file?.uid}`,
              options: [{
                value: file?.uid, key: `avl_doc_${file?.uid}`, name: '', required: false,
                onChange: (event: any): any => onSelectToggle(event, file),
              }],
              size: 20,
              hideLabel: true,
              checked: false,
              isEditableInDetail: true,
            }}
          />}
        </Col>
      </Row>
    </div>
  );
}

export default HISAvailableUploadListItem;
